import { useEffect, useMemo, useState } from 'react'
import {
  FaCheck,
  FaCloudArrowUp,
  FaImage,
  FaMagnifyingGlass,
  FaRotateRight,
  FaXmark,
} from 'react-icons/fa6'
import apiClient, { resolveAssetUrl } from '../../../services/apiClient'

const emptyUpload = {
  title: '',
  caption: '',
  description: '',
  altText: '',
}

function assetLabel(asset) {
  return asset?.title || asset?.originalName || asset?.filename || 'Selected image'
}

export function AdminMediaLibrary({ apiToken, open, onClose, onSelect }) {
  const [assets, setAssets] = useState([])
  const [query, setQuery] = useState('')
  const [selectedId, setSelectedId] = useState('')
  const [selectedFile, setSelectedFile] = useState(null)
  const [uploadFields, setUploadFields] = useState(emptyUpload)
  const [editFields, setEditFields] = useState(emptyUpload)
  const [status, setStatus] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isWorking, setIsWorking] = useState(false)

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.id === selectedId) || null,
    [assets, selectedId]
  )

  const loadAssets = async (nextQuery = query) => {
    if (!apiToken) return

    setIsLoading(true)
    setStatus('')
    try {
      const payload = await apiClient.adminMedia(apiToken, { q: nextQuery, limit: 60 })
      const nextAssets = payload.assets || []
      setAssets(nextAssets)
      setSelectedId((current) => (
        nextAssets.some((asset) => asset.id === current) ? current : nextAssets[0]?.id || ''
      ))
    } catch (error) {
      setStatus(error.message || 'Media library failed to load.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      loadAssets('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, apiToken])

  useEffect(() => {
    if (selectedAsset) {
      setEditFields({
        title: selectedAsset.title || '',
        caption: selectedAsset.caption || '',
        description: selectedAsset.description || '',
        altText: selectedAsset.altText || '',
      })
    }
  }, [selectedAsset])

  if (!open) return null

  const updateUploadField = (field, value) => {
    setUploadFields((current) => ({ ...current, [field]: value }))
  }

  const updateEditField = (field, value) => {
    setEditFields((current) => ({ ...current, [field]: value }))
  }

  const handleSearch = (event) => {
    event.preventDefault()
    event.stopPropagation()
    loadAssets(query)
  }

  const uploadAndSelect = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (!apiToken || !selectedFile) {
      setStatus('Choose an image file first.')
      return
    }

    setIsWorking(true)
    setStatus('')
    try {
      const payload = await apiClient.uploadImage(apiToken, selectedFile, uploadFields)
      const media = payload.media || payload.asset
      setAssets((current) => [media, ...current.filter((asset) => asset.id !== media.id)])
      setSelectedFile(null)
      setUploadFields(emptyUpload)
      onSelect?.(payload.asset)
      onClose?.()
    } catch (error) {
      setStatus(error.message || 'Upload failed.')
    } finally {
      setIsWorking(false)
    }
  }

  const saveSelectedMetadata = async () => {
    if (!apiToken || !selectedAsset) return

    setIsWorking(true)
    setStatus('')
    try {
      const payload = await apiClient.updateMediaAsset(apiToken, selectedAsset.id, editFields)
      setAssets((current) => current.map((asset) => asset.id === selectedAsset.id ? payload.media : asset))
      setStatus('Media details saved.')
    } catch (error) {
      setStatus(error.message || 'Media details could not be saved.')
    } finally {
      setIsWorking(false)
    }
  }

  const useSelectedAsset = async () => {
    if (!apiToken || !selectedAsset) return

    setIsWorking(true)
    setStatus('')
    try {
      const payload = await apiClient.useMediaAsset(apiToken, selectedAsset.id)
      onSelect?.(payload.asset)
      onClose?.()
    } catch (error) {
      setStatus(error.message || 'Media asset could not be selected.')
    } finally {
      setIsWorking(false)
    }
  }

  return (
    <div className="obc-media-modal" role="dialog" aria-modal="true" aria-label="Media library">
      <div className="obc-media-modal__backdrop" onClick={onClose} />
      <section className="obc-media-modal__panel">
        <header className="obc-media-modal__header">
          <div>
            <span>Reusable image library</span>
            <h2>Media Library</h2>
          </div>
          <button type="button" className="obc-media-modal__close" onClick={onClose} aria-label="Close media library">
            <FaXmark />
          </button>
        </header>

        <div className="obc-media-modal__content">
          <aside className="obc-media-upload">
            <div className="obc-media-upload__head">
              <FaCloudArrowUp />
              <div>
                <strong>Upload once</strong>
                <span>Stored in backend uploads and indexed for reuse.</span>
              </div>
            </div>

            <form onSubmit={uploadAndSelect} className="obc-media-upload__form">
              <label className="obc-media-file">
                <FaImage />
                <span>{selectedFile ? selectedFile.name : 'Choose image'}</span>
                <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(event) => setSelectedFile(event.target.files?.[0] || null)} />
              </label>
              <label>Title<input value={uploadFields.title} onChange={(event) => updateUploadField('title', event.target.value)} placeholder="Card title or listing name" /></label>
              <label>Caption<input value={uploadFields.caption} onChange={(event) => updateUploadField('caption', event.target.value)} placeholder="Short visible caption" /></label>
              <label>Description<textarea value={uploadFields.description} onChange={(event) => updateUploadField('description', event.target.value)} placeholder="Condition notes, angle, or reuse context" /></label>
              <label>Alt text<input value={uploadFields.altText} onChange={(event) => updateUploadField('altText', event.target.value)} placeholder="Accessible image description" /></label>
              <button type="submit" disabled={isWorking || !selectedFile}>
                <FaCheck /> Upload and use
              </button>
            </form>
          </aside>

          <main className="obc-media-library">
            <form className="obc-media-library__toolbar" onSubmit={handleSearch}>
              <label>
                <FaMagnifyingGlass />
                <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, caption, filename..." />
              </label>
              <button type="submit"><FaMagnifyingGlass /> Search</button>
              <button type="button" onClick={() => loadAssets(query)}><FaRotateRight /> Refresh</button>
            </form>

            {isLoading ? (
              <div className="obc-media-grid" aria-busy="true">
                {Array.from({ length: 8 }).map((_, index) => <span className="obc-media-card obc-media-card--skeleton" key={index} />)}
              </div>
            ) : assets.length ? (
              <div className="obc-media-grid">
                {assets.map((asset) => (
                  <button
                    type="button"
                    className={`obc-media-card${asset.id === selectedId ? ' is-selected' : ''}`}
                    key={asset.id}
                    onClick={() => setSelectedId(asset.id)}
                  >
                    <img src={resolveAssetUrl(asset.url)} alt={asset.altText || assetLabel(asset)} />
                    <span>{assetLabel(asset)}</span>
                    <small>{asset.caption || asset.originalName}</small>
                  </button>
                ))}
              </div>
            ) : (
              <div className="obc-media-empty">
                <FaImage />
                <strong>No media assets yet</strong>
                <span>Upload one image here, then reuse it across auctions, shop listings, and consignments.</span>
              </div>
            )}
          </main>

          <aside className="obc-media-details">
            {selectedAsset ? (
              <>
                <img src={resolveAssetUrl(selectedAsset.url)} alt={selectedAsset.altText || assetLabel(selectedAsset)} />
                <div>
                  <strong>{assetLabel(selectedAsset)}</strong>
                  <span>{selectedAsset.filename}</span>
                  <small>Used {selectedAsset.usageCount || 0} times</small>
                </div>
                <label>Title<input value={editFields.title} onChange={(event) => updateEditField('title', event.target.value)} /></label>
                <label>Caption<input value={editFields.caption} onChange={(event) => updateEditField('caption', event.target.value)} /></label>
                <label>Description<textarea value={editFields.description} onChange={(event) => updateEditField('description', event.target.value)} /></label>
                <label>Alt text<input value={editFields.altText} onChange={(event) => updateEditField('altText', event.target.value)} /></label>
                <div className="obc-media-details__actions">
                  <button type="button" onClick={saveSelectedMetadata} disabled={isWorking}>Save details</button>
                  <button type="button" onClick={useSelectedAsset} disabled={isWorking}>Use this image</button>
                </div>
              </>
            ) : (
              <div className="obc-media-empty obc-media-empty--compact">
                <FaImage />
                <span>Select an image to view details.</span>
              </div>
            )}
          </aside>
        </div>

        {status ? <p className="obc-media-status">{status}</p> : null}
      </section>
    </div>
  )
}
